import simpleRestProvider from 'ra-data-simple-rest';
import { fetchUtils, DataProvider, HttpError } from 'react-admin';

import { getAccessToken } from '../api/tokenStorage';
import { apiFetch } from '../api/httpClient';
import { API_BASE_URL } from '../api/apiBaseUrl';

/** Strip markup/whitespace from a non-JSON error body so toasts stay one short sentence. */
const summarizeNonJsonBody = (text: string): string => {
    const plain = text
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return plain.length > 300 ? `${plain.slice(0, 300)}…` : plain;
};

/**
 * ASP.NET answers errors in several shapes ({ message }, ProblemDetails { title, detail } and
 * validation { errors: { Field: ["..."] } }). Without this, saves surfaced only "Bad Request".
 */
const errorMessage = (json: any, res: Response): string => {
    if (json && typeof json === 'object') {
        if (typeof json.message === 'string' && json.message.trim()) return json.message.trim();

        const fieldErrors = json.errors && typeof json.errors === 'object' ? json.errors : null;
        if (fieldErrors) {
            const messages = Object.values(fieldErrors)
                .flatMap(value => (Array.isArray(value) ? value : [value]))
                .filter(value => typeof value === 'string' && value.trim())
                .map(value => String(value).trim());
            if (messages.length > 0) return messages.join(' ');
        }

        for (const key of ['detail', 'title', 'error'] as const) {
            const value = json[key];
            if (typeof value === 'string' && value.trim()) return value.trim();
        }
    }
    if (typeof json === 'string' && json.trim()) return json.trim();
    return res.statusText || 'Http Error';
};

const httpClient = (url: string, options: any = {}) => {
    // Use the same refresh-on-401 logic as the app auth client.
    // This prevents "stuck" 401s that make saves look like they do nothing.
    const headers = new Headers(options.headers ?? {});
    headers.set('Accept', 'application/json');
    // Ensure JSON writes send correct content type (fixes 415 on PUT/POST)
    if (options?.body != null && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    const token = getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    // Prefer apiFetch when calling our backend to allow refresh flow.
    // Note: `''.startsWith('')` is true in JS, so treat empty base + `/api` paths explicitly.
    const isOurApi =
        (API_BASE_URL !== '' && url.startsWith(API_BASE_URL)) ||
        (API_BASE_URL === '' && url.startsWith('/api'));
    if (isOurApi) {
        const path = API_BASE_URL !== '' ? url.slice(API_BASE_URL.length) : url;
        return apiFetch(
            path,
            {
                ...options,
                headers,
            },
            { auth: true, retryOn401: true }
        ).then(async res => {
            const text = await res.text();
            let json: any = {};
            try {
                json = text ? JSON.parse(text) : {};
            } catch {
                // Non-JSON body (e.g. the ASP.NET developer error page): keep it readable.
                json = text ? { message: summarizeNonJsonBody(text) } : {};
            }
            const out = {
                status: res.status,
                headers: res.headers,
                body: text,
                json,
            };
            if (res.status < 200 || res.status >= 300) {
                throw new HttpError(errorMessage(json, res), res.status, json);
            }
            return out;
        });
    }

    return fetchUtils.fetchJson(url, { ...options, headers, credentials: 'include' });
};

export const backendDataProvider: DataProvider = simpleRestProvider(
    `${API_BASE_URL}/api`,
    httpClient
);

